import React, { useState, useMemo } from 'react';
import {
  FileSearch,
  Download,
  Filter,
  RefreshCw,
  AlertTriangle,
  Shield,
  User,
  Clock,
  Activity,
  Eye,
  Search,
  LogIn,
  FileText,
  Trash2,
  Edit,
  Plus,
  Loader2,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Users,
  Monitor,
  AlertCircle,
} from 'lucide-react';
import {
  useAuditLogs,
  useAuditSummary,
  useAuditFilterOptions,
  useExportAuditLogs,
} from '../../hooks/useAuditLogs';
import AuditDetailModal from '../../components/admin/AuditDetailModal';
import { formatDateTime, formatDate } from '../../utils/formatters';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// Action type icons
const ACTION_ICONS = {
  VIEW: Eye,
  SEARCH: Search,
  EXPORT: Download,
  CREATE: Plus,
  UPDATE: Edit,
  DELETE: Trash2,
  LOGIN: LogIn,
  LOGOUT: LogIn,
};

// Action type colors
const ACTION_COLORS = {
  VIEW: 'bg-blue-100 text-blue-700',
  SEARCH: 'bg-purple-100 text-purple-700',
  EXPORT: 'bg-green-100 text-green-700',
  CREATE: 'bg-emerald-100 text-emerald-700',
  UPDATE: 'bg-yellow-100 text-yellow-700',
  DELETE: 'bg-red-100 text-red-700',
  LOGIN: 'bg-cyan-100 text-cyan-700',
  LOGOUT: 'bg-gray-100 text-gray-700',
};

function StatCard({ title, value, icon: Icon, color = 'blue', subtitle }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value?.toLocaleString() || 0}</p>
          <p className="text-sm text-gray-500">{title}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

function ActionBadge({ actionType }) {
  const Icon = ACTION_ICONS[actionType] || Activity;
  const colorClass = ACTION_COLORS[actionType] || 'bg-gray-100 text-gray-700';

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      <Icon className="w-3 h-3" />
      {actionType}
    </span>
  );
}

function PHIBadge({ accessedPhi, accessedPii }) {
  if (!accessedPhi && !accessedPii) return null;

  return (
    <div className="flex gap-1">
      {accessedPhi && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <Shield className="w-3 h-3" />
          PHI
        </span>
      )}
      {accessedPii && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
          <AlertTriangle className="w-3 h-3" />
          PII
        </span>
      )}
    </div>
  );
}

export default function AuditLogs() {
  // Filter state
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    userEmail: '',
    actionType: '',
    actionCategory: '',
    screenName: '',
    accessedPhi: '',
    accessedPii: '',
    limit: 50,
    offset: 0,
  });

  const [showFilters, setShowFilters] = useState(true);
  const [activeTab, setActiveTab] = useState('logs'); // 'logs' | 'summary'
  const [selectedAuditId, setSelectedAuditId] = useState(null);

  // Fetch data
  const { data: logsData, isLoading: logsLoading, refetch: refetchLogs } = useAuditLogs(filters);
  const { data: summaryData, isLoading: summaryLoading } = useAuditSummary(
    filters.startDate,
    filters.endDate
  );
  const { data: filterOptionsData } = useAuditFilterOptions();
  const exportMutation = useExportAuditLogs();

  const logs = logsData?.data?.records || [];
  const totalRecords = logsData?.data?.total || 0;
  const summary = summaryData?.data?.summary || {};
  const actionBreakdown = summaryData?.data?.actionBreakdown || [];
  const topUsers = summaryData?.data?.topUsers || [];
  const activityByHour = summaryData?.data?.activityByHour || [];
  const screenBreakdown = summaryData?.data?.screenBreakdown || [];
  const filterOptions = filterOptionsData?.data || {};

  // Calculate pagination
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;
  const totalPages = Math.ceil(totalRecords / filters.limit);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      offset: 0, // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({
      ...prev,
      offset: (newPage - 1) * prev.limit,
    }));
  };

  const handleExport = () => {
    exportMutation.mutate({ filters, format: 'csv' });
  };

  const handleRefresh = () => {
    refetchLogs();
  };

  // Prepare chart data
  const actionChartData = useMemo(() => {
    return actionBreakdown.map((item, idx) => ({
      name: item.action_type,
      value: parseInt(item.count),
      fill: COLORS[idx % COLORS.length],
    }));
  }, [actionBreakdown]);

  const hourlyChartData = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour: i.toString().padStart(2, '0') + ':00',
      count: 0,
    }));
    activityByHour.forEach((item) => {
      const hourIdx = parseInt(item.hour);
      if (hourIdx >= 0 && hourIdx < 24) {
        hours[hourIdx].count = parseInt(item.count);
      }
    });
    return hours;
  }, [activityByHour]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileSearch className="w-6 h-6 text-blue-600" />
            Audit Logs
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            HITRUST compliant activity tracking for PII/PHI access
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              showFilters
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button
            onClick={handleRefresh}
            disabled={logsLoading}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={exportMutation.isPending || logs.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {exportMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('logs')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'logs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Activity Logs
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'summary'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Summary & Analytics
          </button>
        </nav>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* User Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
              <input
                type="text"
                value={filters.userEmail}
                onChange={(e) => handleFilterChange('userEmail', e.target.value)}
                placeholder="Search by email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Action Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
              <select
                value={filters.actionType}
                onChange={(e) => handleFilterChange('actionType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Actions</option>
                {(filterOptions.actionTypes || []).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Action Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={filters.actionCategory}
                onChange={(e) => handleFilterChange('actionCategory', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {(filterOptions.actionCategories || []).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Screen Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Screen</label>
              <select
                value={filters.screenName}
                onChange={(e) => handleFilterChange('screenName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Screens</option>
                {(filterOptions.screenNames || []).map((screen) => (
                  <option key={screen} value={screen}>
                    {screen}
                  </option>
                ))}
              </select>
            </div>

            {/* PHI Access */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PHI Access</label>
              <select
                value={filters.accessedPhi}
                onChange={(e) => handleFilterChange('accessedPhi', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="true">PHI Accessed</option>
                <option value="false">No PHI</option>
              </select>
            </div>

            {/* PII Access */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PII Access</label>
              <select
                value={filters.accessedPii}
                onChange={(e) => handleFilterChange('accessedPii', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="true">PII Accessed</option>
                <option value="false">No PII</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'summary' && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <StatCard
              title="Total Events"
              value={summary.total_events}
              icon={Activity}
              color="blue"
            />
            <StatCard
              title="Unique Users"
              value={summary.unique_users}
              icon={Users}
              color="green"
            />
            <StatCard
              title="Sessions"
              value={summary.unique_sessions}
              icon={Monitor}
              color="purple"
            />
            <StatCard
              title="PHI Access"
              value={summary.phi_access_count}
              icon={Shield}
              color="red"
            />
            <StatCard
              title="PII Access"
              value={summary.pii_access_count}
              icon={AlertTriangle}
              color="yellow"
            />
            <StatCard
              title="Exports"
              value={summary.export_count}
              icon={Download}
              color="green"
            />
            <StatCard
              title="Errors"
              value={summary.error_count}
              icon={AlertCircle}
              color="red"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Action Type Distribution */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Action Type Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={actionChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {actionChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Activity by Hour */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Activity by Hour</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" name="Events" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Users & Screen Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Users */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Top Users by Activity</h3>
              <div className="space-y-2">
                {topUsers.map((user, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{user.user_email}</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-blue-600">
                      {parseInt(user.event_count).toLocaleString()}
                    </span>
                  </div>
                ))}
                {topUsers.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No data available</p>
                )}
              </div>
            </div>

            {/* Screen Breakdown */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Most Accessed Screens</h3>
              <div className="space-y-2">
                {screenBreakdown.map((screen, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{screen.screen_name || 'Unknown'}</span>
                    </div>
                    <span className="text-sm font-semibold text-blue-600">
                      {parseInt(screen.count).toLocaleString()}
                    </span>
                  </div>
                ))}
                {screenBreakdown.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No data available</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'logs' && (
        <>
          {/* Results Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700">
                Audit Log Records ({totalRecords.toLocaleString()} total)
              </h3>
              <div className="text-sm text-gray-500">
                Showing {filters.offset + 1} - {Math.min(filters.offset + filters.limit, totalRecords)}
              </div>
            </div>

            {logsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <FileSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No audit logs found for the selected filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Timestamp
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Action
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Screen / Resource
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Data Access
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        IP Address
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log.audit_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(log.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {log.user_name || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">{log.user_email}</div>
                          {log.user_role && (
                            <span className="text-xs text-gray-400">{log.user_role}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ActionBadge actionType={log.action_type} />
                          {log.action_category && (
                            <div className="text-xs text-gray-400 mt-1">{log.action_category}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{log.screen_name || '-'}</div>
                          {log.resource_type && (
                            <div className="text-xs text-gray-500">
                              {log.resource_type}
                              {log.resource_id && `: ${log.resource_id}`}
                            </div>
                          )}
                          {log.query_type && (
                            <div className="text-xs text-blue-600">{log.query_type}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <PHIBadge accessedPhi={log.accessed_phi} accessedPii={log.accessed_pii} />
                          {log.records_accessed > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {log.records_accessed} records
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              log.response_status < 400
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {log.response_status || '-'}
                          </span>
                          {log.response_time_ms && (
                            <div className="text-xs text-gray-400">{log.response_time_ms}ms</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                          {log.ip_address || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setSelectedAuditId(log.audit_id)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Rows per page:</span>
                  <select
                    value={filters.limit}
                    onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Audit Detail Modal */}
      {selectedAuditId && (
        <AuditDetailModal
          auditId={selectedAuditId}
          onClose={() => setSelectedAuditId(null)}
        />
      )}
    </div>
  );
}
